/**
 * Intelligence Workspace — the primary interface for the Radiology Intelligence Platform.
 *
 * Three-panel layout:
 *   - Left rail: Patient timeline
 *   - Center: Query bar + answer canvas with reasoning steps
 *   - Right rail: Evidence panel with source references
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  LinearProgress,
  Autocomplete,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TimelineIcon from '@mui/icons-material/Timeline';
import ArticleIcon from '@mui/icons-material/Article';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReactMarkdown from 'react-markdown';

import ReasoningPlanView from '../components/ReasoningPlanView';
import MeasurementDeltaChart from '../components/MeasurementDeltaChart';
import ScienceIcon from '@mui/icons-material/Science';

import { useAppDispatch } from '../hooks/reduxHooks';
import { submitNLQQuery } from '../store/nlqSlice';
import type { NLQQueryResponse, NLQEntity } from '../services/nlqService';

import aiCopilotService, {
  AIQueryResponse,
  AIQueryContext,
  SourceReference,
  ReasoningStep,
  AIStreamEvent,
} from '../services/aiCopilotService';
import apiClient from '../services/apiClient';
import reportService, { ReportResponse } from '../services/reportService';
import { API_URL } from '../config';
import { typography } from '~/theme';

// ── Types ──────────────────────────────────────────────────────────────────

interface PatientOption {
  id: string;
  label: string;
  mrn?: string;
}

interface TimelineEntry {
  date: string;
  modality?: string;
  description?: string;
  session_id?: string;
  report_id?: number;
  report_summary?: Record<string, unknown> | null;
  highlighted?: boolean;
}

interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
  response?: AIQueryResponse | null;
  nlqResult?: NLQQueryResponse | null;
  reasoningSteps?: ReasoningStep[];
  loading?: boolean;
  error?: string | null;
}

// Intents returned by the NLQ backend that indicate the query needs the AI
// Copilot's streaming multi-step reasoning pipeline instead.  These are
// free-form clinical reasoning queries ("has cancer progressed?",
// "what does this finding mean?") that the tag-based NLQ DB layer cannot
// meaningfully answer.
const NLQ_COPILOT_INTENTS = new Set([
  'clinical_explanation',
  'general_question',
  'progression_since_treatment',
  'progression_analysis',
  'study_comparison',
  'addendum_request',
  'critical_finding',
  'patient_timeline',
  'quality_check',
  'operational_query',
  'statistical',
]);

const isCriticalFindingQuery = (query: string) =>
  /\b(?:critical|urgent|life[- ]threatening|stat)\s+(?:finding|findings)\b|\bflag\s+(?:this\s+)?report\b/i.test(query);

const isOperationalAggregationQuery = (query: string) =>
  /\b(?:daily\s+(?:breakup|breakdown|count)|(?:breakup|breakdown)\s+(?:by\s+(?:day|date|study\s+date|modality|radiologist)|of\s+(?:reports?|studies?))|(?:reports?|studies?|dictations?|transcriptions?)\s+(?:per|each)\s+(?:day|date|modality|radiologist|user|doctor)|how\s+many\s+(?:reports?|studies?|dictations?|transcriptions?)|workload\s+per\s+(?:radiologist|doctor|physician)|average\s+turnaround|report\s+count\s+by)\b/i.test(query);


interface PatientSearchResult {
  patient_id?: string;
  id?: string;
  hash_id?: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  mrn?: string;
}

interface TimelineHistoryItem {
  study_date?: string;
  created_at?: string;
  modality?: string;
  study_description?: string;
  description?: string;
  exam_name?: string;
  session_id?: string;
  report_id?: number;
  report_summary?: Record<string, unknown> | null;
}

// ── Patient Timeline Rail (Left) ──────────────────────────────────────────

interface TimelineRailProps {
  entries: TimelineEntry[];
  loading: boolean;
  highlightedSessionIds: Set<string>;
  selectedSessionId?: string | null;
  onEntryClick?: (entry: TimelineEntry) => void;
}

const TimelineRail: React.FC<TimelineRailProps> = ({
  entries,
  loading,
  highlightedSessionIds,
  selectedSessionId,
  onEntryClick,
}) => (
  <Box
    sx={{
      width: 280,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      borderRight: '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper',
      overflow: 'hidden',
    }}
  >
    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TimelineIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="subtitle2" fontWeight={typography.label.fontWeight}>
          Imaging Timeline
        </Typography>
      </Box>
    </Box>

    <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {!loading && entries.length === 0 && (
        <Typography variant="caption" color="text.disabled" sx={{ px: 2, display: 'block', mt: 2 }}>
          Select a patient to view their imaging timeline
        </Typography>
      )}

      {entries.map((entry, i) => {
        const isHighlighted = entry.session_id ? highlightedSessionIds.has(entry.session_id) : false;
        const isSelected = entry.session_id ? selectedSessionId === entry.session_id : false;
        return (
          <Box
            key={i}
            onClick={() => onEntryClick?.(entry)}
            sx={{
              px: 2,
              py: 0.75,
              borderLeft: '3px solid',
              borderLeftColor: isHighlighted || isSelected ? 'primary.main' : 'transparent',
              bgcolor: isSelected ? 'primary.100' : isHighlighted ? 'primary.50' : 'transparent',
              transition: 'all 0.2s',
              cursor: onEntryClick ? 'pointer' : 'default',
              '&:hover': { bgcolor: onEntryClick ? 'grey.50' : undefined },
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
              {entry.date}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.3 }}>
              {entry.modality && (
                <Chip
                  label={entry.modality}
                  size="small"
                  sx={{ height: 16, fontSize: '0.62rem', mr: 0.5 }}
                />
              )}
              {entry.description || 'Study'}
            </Typography>
          </Box>
        );
      })}
    </Box>
  </Box>
);

// ── Study Detail Dialog ─────────────────────────────────────────────────

interface StudyDetailDialogProps {
  open: boolean;
  entry: TimelineEntry | null;
  report: ReportResponse | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
}

const StudyDetailDialog: React.FC<StudyDetailDialogProps> = ({
  open,
  entry,
  report,
  loading,
  error,
  onClose,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
    <DialogTitle>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ArticleIcon sx={{ color: 'primary.main', fontSize: 22 }} />
        <Typography variant="h6" component="span" sx={{ fontSize: '1rem' }}>
          {entry?.modality || 'Study'} {entry?.description ? `— ${entry.description}` : ''}
        </Typography>
      </Box>
      {entry?.date && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          {entry.date}
        </Typography>
      )}
    </DialogTitle>
    <DialogContent dividers>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {!loading && !error && report && (
        <Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip label={`Status: ${report.status}`} size="small" variant="outlined" />
            {report.cpt_exam_name && (
              <Chip label={report.cpt_exam_name} size="small" variant="outlined" />
            )}
            {report.diagnosis_category && (
              <Chip label={report.diagnosis_category} size="small" variant="outlined" />
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ typography: 'body2', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            <ReactMarkdown>{report.report_text}</ReactMarkdown>
          </Box>
        </Box>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} variant="outlined" size="small">
        Close
      </Button>
      {report?.session_id && (
        <Button
          component="a"
          href={`/transcribe?session=${encodeURIComponent(report.session_id)}`}
          variant="contained"
          size="small"
          startIcon={<OpenInNewIcon />}
        >
          Open Report Editor
        </Button>
      )}
    </DialogActions>
  </Dialog>
);

// ── Evidence Panel (Right) ────────────────────────────────────────────────

interface EvidencePanelProps {
  sources: SourceReference[];
  confidence: number;
}

const EvidencePanel: React.FC<EvidencePanelProps> = ({ sources, confidence }) => (
  <Box
    sx={{
      width: 320,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      borderLeft: '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper',
      overflow: 'hidden',
    }}
  >
    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ArticleIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
        <Typography variant="subtitle2" fontWeight={typography.label.fontWeight}>
          Evidence
        </Typography>
        {sources.length > 0 && (
          <Chip
            label={sources.length}
            size="small"
            color="primary"
            sx={{ height: 18, fontSize: '0.65rem', ml: 'auto' }}
          />
        )}
      </Box>
      {confidence > 0 && (
        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Confidence:
          </Typography>
          <Chip
            label={`${Math.round(confidence * 100)}%`}
            size="small"
            color={confidence >= 0.7 ? 'success' : confidence >= 0.4 ? 'warning' : 'error'}
            variant="outlined"
            sx={{ height: 18, fontSize: '0.62rem' }}
          />
        </Box>
      )}
    </Box>

    <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
      {sources.length === 0 && (
        <Typography variant="caption" color="text.disabled" sx={{ px: 2, display: 'block', mt: 2 }}>
          Evidence sources will appear here after a query
        </Typography>
      )}

      {sources.map((src, i) => (
        <Box
          key={i}
          sx={{
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'grey.50' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
            <Chip
              label={src.source_type.replace(/_/g, ' ')}
              size="small"
              variant="filled"
              color="default"
              sx={{ height: 16, fontSize: '0.6rem', textTransform: 'capitalize' }}
            />
            {!!src.metadata?.study_date && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>
                {src.metadata.study_date as string}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              {src.label}
            </Typography>
            {!!src.metadata?.session_id && (
              <Tooltip title="Open in report editor">
                <IconButton
                  size="small"
                  href={`/transcribe?session=${encodeURIComponent(src.metadata.session_id as string)}`}
                  sx={{ p: 0.25 }}
                >
                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

// ── Main Intelligence Page ────────────────────────────────────────────────

const IntelligencePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [streamController, setStreamController] = useState<{ close: () => void } | null>(null);

  // Patient state
  const [patientOptions, setPatientOptions] = useState<PatientOption[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<PatientOption | null>(null);
  const [patientSearchLoading, setPatientSearchLoading] = useState(false);

  // Timeline state
  const [timelineEntries, setTimelineEntries] = useState<TimelineEntry[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // Study detail state
  const [selectedStudyEntry, setSelectedStudyEntry] = useState<TimelineEntry | null>(null);
  const [selectedStudyReport, setSelectedStudyReport] = useState<ReportResponse | null>(null);
  const [studyDetailLoading, setStudyDetailLoading] = useState(false);
  const [studyDetailError, setStudyDetailError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  // Derive latest assistant response and highlighted session IDs from conversation
  const latestAssistantResponse = React.useMemo(() => {
    return [...messages].reverse().find((m) => m.role === 'assistant')?.response || null;
  }, [messages]);

  const highlightedSessionIds = React.useMemo(() => {
    const sources = latestAssistantResponse?.sources || [];
    const ids = new Set<string>();
    sources.forEach((s: SourceReference) => {
      if (s.metadata?.session_id) ids.add(String(s.metadata.session_id));
    });
    return ids;
  }, [latestAssistantResponse]);

  // Patient search
  const handlePatientSearch = useCallback(async (searchText: string) => {
    if (searchText.length < 2) {
      setPatientOptions([]);
      return;
    }
    setPatientSearchLoading(true);
    try {
      const res = await apiClient.get(`${API_URL}/patients/search`, {
        params: { name: searchText, limit: 10 },
      });
      const patients = (res.data?.patients || res.data || []).map((p: PatientSearchResult) => ({
        id: p.patient_id || p.id || p.hash_id,
        label: p.display_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.name || `Patient ${p.patient_id || p.id}`,
        mrn: p.mrn,
      }));
      setPatientOptions(patients);
    } catch {
      setPatientOptions([]);
    } finally {
      setPatientSearchLoading(false);
    }
  }, []);

  // Load patient timeline
  useEffect(() => {
    if (!selectedPatient) {
      setTimelineEntries([]);
      return;
    }
    const loadTimeline = async () => {
      setTimelineLoading(true);
      try {
        const res = await apiClient.get(`${API_URL}/patients/${selectedPatient.id}/history`);
        const history = res.data?.reports || res.data?.history || res.data || [];
        const entries: TimelineEntry[] = history.map((h: TimelineHistoryItem) => ({
          date: h.study_date || h.created_at || '',
          modality: h.modality,
          description: h.study_description || h.description || h.exam_name,
          session_id: h.session_id,
          report_id: h.report_id,
          report_summary: h.report_summary,
        }));
        setTimelineEntries(entries);
      } catch {
        setTimelineEntries([]);
      } finally {
        setTimelineLoading(false);
      }
    };
    loadTimeline();
  }, [selectedPatient]);

  // Open study detail dialog and fetch the full report for the selected session.
  const handleStudyClick = useCallback(async (entry: TimelineEntry) => {
    setSelectedStudyEntry(entry);
    setSelectedStudyReport(null);
    setStudyDetailError(null);
    setStudyDetailLoading(true);
    try {
      if (!entry.session_id) {
        throw new Error('No session ID for this study.');
      }
      const report = await reportService.getReport(entry.session_id);
      setSelectedStudyReport(report);
    } catch (err) {
      setStudyDetailError(err instanceof Error ? err.message : 'Failed to load study details.');
    } finally {
      setStudyDetailLoading(false);
    }
  }, []);

  const handleCloseStudyDetail = useCallback(() => {
    setSelectedStudyEntry(null);
    setSelectedStudyReport(null);
    setStudyDetailError(null);
  }, []);

  // Fallback to a regular POST if streaming is unavailable or fails.
  const runFallbackQuery = useCallback(async (request: { query: string; context: AIQueryContext; conversation_id?: string }) => {
    try {
      const result = await aiCopilotService.submitQuery(request);
      if (result.metadata?.conversation_id) {
        setConversationId(result.metadata.conversation_id as string);
      }
      setMessages((prev) => {
        const next = prev.slice();
        if (next.length > 0 && next[next.length - 1].role === 'assistant') {
          next[next.length - 1] = {
            role: 'assistant',
            content: result.answer,
            response: result,
            reasoningSteps: result.reasoning_steps || [],
            loading: false,
          };
        }
        return next;
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to get response';
      setMessages((prev) => {
        const next = prev.slice();
        if (next.length > 0 && next[next.length - 1].role === 'assistant') {
          next[next.length - 1] = { ...next[next.length - 1], error: message, loading: false };
        }
        return next;
      });
      setError(message);
    } finally {
      setLoading(false);
      setStreamController(null);
    }
  }, []);

  // Submit query and append to the ongoing conversation.
  const handleSubmit = useCallback(async (queryText?: string) => {
    const q = (queryText || query).trim();
    if (!q || loading) return;

    // Close any existing stream
    if (streamController) {
      streamController.close();
    }

    setQuery('');
    setError(null);
    setLoading(true);

    // ── Step 1: All queries go to NLQ for LLM intent classification. ──────
    // The backend classifies intent, extracts entities, and executes tag-based
    // DB queries where applicable.  If it returns a copilot-class intent
    // (clinical_explanation, progression_analysis, etc.) we escalate to the
    // AI Copilot streaming pipeline in Step 2.
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: q },
      { role: 'assistant', content: '', nlqResult: null, loading: true },
    ]);

    let nlqResponse: NLQQueryResponse | null = null;
    try {
      const result = await dispatch(submitNLQQuery({
        query: q,
        patient_id: selectedPatient?.id || undefined,
        limit: 20,
      })).unwrap();
      nlqResponse = result.response;
    } catch (err: unknown) {
      const msg = (err as Error)?.message || 'NLQ query failed';
      setMessages((prev) => {
        const next = prev.slice();
        const last = next[next.length - 1];
        if (last?.role === 'assistant') {
          next[next.length - 1] = { ...last, error: msg, loading: false };
        }
        return next;
      });
      setLoading(false);
      return;
    }

    // ── Step 2: Escalate to AI Copilot for reasoning-class intents. ────────
    // NLQ_COPILOT_INTENTS are returned by the backend when the query requires
    // multi-step clinical reasoning that tag-based DB queries cannot answer.
    if (nlqResponse && (
      NLQ_COPILOT_INTENTS.has(nlqResponse.intent) ||
      isCriticalFindingQuery(q) ||
      isOperationalAggregationQuery(q)
    )) {
      // Replace placeholder with a streaming copilot message
      setMessages((prev) => {
        const next = prev.slice();
        const last = next[next.length - 1];
        if (last?.role === 'assistant') {
          next[next.length - 1] = { ...last, nlqResult: null, reasoningSteps: [], loading: true };
        }
        return next;
      });

      const context: AIQueryContext = {};
      if (selectedPatient) context.patient_id = selectedPatient.id;
      if (selectedStudyEntry?.session_id) context.session_id = selectedStudyEntry.session_id;

      const request = {
        query: q,
        context,
        conversation_id: conversationId || undefined,
      };

      const pendingSteps = new Map<string, ReasoningStep>();
      const updateLastAssistant = (patch: Partial<ConversationMessage>) => {
        setMessages((prev) => {
          const next = prev.slice();
          const lastIndex = next.length - 1;
          if (lastIndex >= 0 && next[lastIndex].role === 'assistant') {
            next[lastIndex] = { ...next[lastIndex], ...patch };
          }
          return next;
        });
      };
      const updateStep = (name: string, patch: Partial<ReasoningStep>) => {
        const existing = pendingSteps.get(name);
        const step: ReasoningStep = {
          name,
          action: patch.action ?? existing?.action ?? '',
          success: patch.success ?? existing?.success ?? true,
          latency_ms: patch.latency_ms ?? existing?.latency_ms ?? 0,
          error: patch.error ?? existing?.error ?? null,
        };
        pendingSteps.set(name, step);
        updateLastAssistant({ reasoningSteps: Array.from(pendingSteps.values()) });
      };

      const stream = aiCopilotService.streamQuery(
        request,
        (event: AIStreamEvent) => {
          if (event.event === 'plan' && event.steps) {
            event.steps.forEach((s: { name: string; action: string }) => {
              updateStep(s.name, { action: s.action });
            });
          } else if (event.event === 'step_start' && event.step) {
            updateStep(event.step, { action: event.action ?? '' });
          } else if (event.event === 'step_complete' && event.step) {
            updateStep(event.step, {
              action: event.action ?? '',
              success: event.success ?? true,
              latency_ms: event.latency_ms ?? 0,
              error: event.error ?? null,
            });
          } else if (event.event === 'agent_step' && event.step) {
            updateStep(event.step, {
              action: event.action ?? 'agent',
              success: event.success ?? true,
              latency_ms: event.latency_ms ?? 0,
              error: event.error ?? null,
            });
          } else if (event.event === 'complete') {
            if (event.response) {
              updateLastAssistant({
                content: event.response.answer,
                response: event.response,
                reasoningSteps: event.response.reasoning_steps || Array.from(pendingSteps.values()),
                loading: false,
              });
              if (event.response.metadata?.conversation_id) {
                setConversationId(event.response.metadata.conversation_id as string);
              }
            } else if (event.answer) {
              updateLastAssistant({
                content: event.answer,
                response: {
                  answer: event.answer,
                  sources: [],
                  confidence: 0.7,
                  intent: '',
                  follow_up_suggestions: [],
                  reasoning_steps: Array.from(pendingSteps.values()),
                  metadata: event.metadata ?? {},
                },
                reasoningSteps: Array.from(pendingSteps.values()),
                loading: false,
              });
            }
            setLoading(false);
            setStreamController(null);
          } else if (event.event === 'error') {
            const errMsg = event.error || 'Streaming error';
            setError(errMsg);
            updateLastAssistant({ error: errMsg, loading: false });
            setLoading(false);
            setStreamController(null);
          }
        },
        () => { runFallbackQuery(request); }
      );
      setStreamController(stream);
      return;
    }

    // ── Step 3: Structured NLQ result — render inline card. ────────────────
    setMessages((prev) => {
      const next = prev.slice();
      const last = next[next.length - 1];
      if (last?.role === 'assistant') {
        next[next.length - 1] = {
          ...last,
          nlqResult: nlqResponse,
          content: nlqResponse?.answer ?? '',
          loading: false,
        };
      }
      return next;
    });
    setLoading(false);
  }, [query, loading, selectedPatient, selectedStudyEntry, conversationId, streamController, runFallbackQuery, dispatch]);

  // Clean up any active stream when the component unmounts or patient changes.
  useEffect(() => {
    return () => {
      if (streamController) {
        streamController.close();
      }
    };
  }, [streamController]);

  // Reset study selection and stop any in-flight stream when the patient changes.
  useEffect(() => {
    setSelectedStudyEntry(null);
    setSelectedStudyReport(null);
    setStudyDetailError(null);
    if (streamController) {
      streamController.close();
      setStreamController(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  const handleFollowUp = useCallback((suggestion: string) => {
    handleSubmit(suggestion);
  }, [handleSubmit]);

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left Rail — Patient Timeline */}
      <TimelineRail
        entries={timelineEntries}
        loading={timelineLoading}
        highlightedSessionIds={highlightedSessionIds}
        selectedSessionId={selectedStudyEntry?.session_id}
        onEntryClick={handleStudyClick}
      />

      {/* Center — Query + Answer Canvas */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Patient Selector */}
        <Box sx={{ px: 3, pt: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Autocomplete
            size="small"
            options={patientOptions}
            value={selectedPatient}
            loading={patientSearchLoading}
            getOptionLabel={(opt) => opt.label + (opt.mrn ? ` (MRN: ${opt.mrn})` : '')}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            onChange={(_, value) => {
              setSelectedPatient(value);
              setMessages([]);
              setConversationId(null);
            }}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') handlePatientSearch(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search patient by name or MRN..."
                variant="outlined"
                sx={{ '& .MuiInputBase-root': { fontSize: '0.85rem' } }}
              />
            )}
            noOptionsText="Type to search patients"
            sx={{ maxWidth: 400 }}
          />
        </Box>

        {/* Answer area */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
          {messages.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', mt: 8 }}>
              <AutoAwesomeIcon sx={{ fontSize: 56, color: 'grey.300', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Radiology Intelligence
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 480, mx: 'auto' }}>
                Ask complex clinical questions. The platform plans multi-step workflows,
                gathers evidence from multiple sources, and provides actionable insights
                with full transparency into how the answer was reached.
              </Typography>
              <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {[
                  'Has this patient\'s cancer progressed since treatment?',
                  'Compare the last two MRI studies',
                  'Are there any critical findings?',
                  'How many reports were finalized this week?',
                ].map((suggestion, i) => (
                  <Chip
                    key={i}
                    label={suggestion}
                    variant="outlined"
                    color="primary"
                    clickable
                    onClick={() => {
                      setQuery(suggestion);
                      inputRef.current?.focus();
                    }}
                    sx={{ fontSize: '0.75rem' }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {messages.map((msg, index) => {
            if (msg.role === 'user') {
              return (
                <Typography
                  key={index}
                  variant="body1"
                  sx={{ mb: 2, px: 1, py: 0.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                >
                  <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Q:</Box>
                  {msg.content}
                </Typography>
              );
            }

            const response = msg.response;
            const nlqResult = msg.nlqResult;
            const reasoningSteps = msg.reasoningSteps || [];
            const isLastAssistant = index === messages.length - 1 && msg.role === 'assistant';

            // ── Inline NLQ result card ──────────────────────────────────
            if (nlqResult && !msg.loading) {
              return (
                <Box key={index} sx={{ mb: 3 }}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ScienceIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      <Typography variant="subtitle2" fontWeight={typography.label.fontWeight}>
                        Intelligence Answer
                      </Typography>
                      <Chip
                        label={nlqResult.intent.replace(/_/g, ' ')}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.62rem' }}
                      />
                      <Chip
                        label={nlqResult.source}
                        size="small"
                        variant="outlined"
                        color={nlqResult.source === 'llm' ? 'primary' : 'default'}
                        sx={{ height: 18, fontSize: '0.62rem' }}
                      />
                      {nlqResult.query_metadata?.distinct_patient_count !== undefined &&
                        nlqResult.query_metadata?.distinct_patient_count !== null && (
                          <Chip
                            label="population query"
                            size="small"
                            variant="outlined"
                            color="warning"
                            sx={{ height: 18, fontSize: '0.62rem' }}
                          />
                        )}
                      <Chip
                        label={`${Math.round(nlqResult.confidence * 100)}%`}
                        size="small"
                        variant="outlined"
                        color={nlqResult.confidence >= 0.7 ? 'success' : 'warning'}
                        sx={{ height: 18, fontSize: '0.62rem', ml: 'auto' }}
                      />
                    </Box>

                    {nlqResult.query_metadata?.hipaa_safe_harbor_applied && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        Results suppressed to protect patient privacy (HIPAA safe harbor).
                      </Alert>
                    )}

                    <Box sx={{ lineHeight: 1.6, '& p': { typography: 'body2', mb: 0.75, mt: 0 }, '& ul': { pl: 2.5, mb: 0.75 }, '& ol': { pl: 2.5, mb: 0.75 }, '& li': { typography: 'body2', mb: 0.25 }, '& strong': { fontWeight: typography.label.fontWeight } }}>
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => <Typography variant="body2" sx={{ mb: 0.75, mt: 0, lineHeight: 1.6 }}>{children}</Typography>,
                          strong: ({ children }) => <Typography component="span" variant="body2" fontWeight={typography.label.fontWeight}>{children}</Typography>,
                        }}
                      >
                        {nlqResult.answer}
                      </ReactMarkdown>
                    </Box>

                    {nlqResult.entities.length > 0 && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Extracted entities:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {nlqResult.entities.map((e: NLQEntity, i: number) => (
                            <Tooltip
                              key={i}
                              title={e.snomed_code ? `SNOMED: ${e.snomed_code}` : ''}
                            >
                              <Chip
                                label={`${e.entity_type}: ${e.canonical_value}`}
                                size="small"
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      </Box>
                    )}

                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {nlqResult.count !== null && (
                        <Typography variant="caption" color="text.secondary">
                          {(() => {
                            const subject = nlqResult.query_metadata?.query_subject as string | undefined;
                            if (subject === 'clinical' || subject === 'reports') {
                              return 'Reports';
                            }
                            if (subject) {
                              return subject.charAt(0).toUpperCase() + subject.slice(1);
                            }
                            return nlqResult.query_metadata?.is_patient_scoped ? 'Reports' : 'Patients';
                          })()}:{' '}
                          <strong>{nlqResult.count}</strong>
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Latency: <strong>{nlqResult.latency_ms}ms</strong>
                      </Typography>
                      {!nlqResult.query_metadata?.hipaa_suppressed &&
                        nlqResult.query_metadata?.distinct_patient_count !== null &&
                        nlqResult.query_metadata?.distinct_patient_count !== undefined &&
                        nlqResult.query_metadata?.distinct_patient_count !== nlqResult.count && (
                          <Typography variant="caption" color="text.secondary">
                            Patients: <strong>{nlqResult.query_metadata.distinct_patient_count}</strong>
                          </Typography>
                        )}
                    </Box>

                    <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span style={{ color: 'green', fontWeight: 700 }}>●</span>
                        RadNeo never sends PII to any external systems, including LLMs.
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              );
            }

            return (
              <Box key={index} sx={{ mb: 3 }}>
                {msg.loading && isLastAssistant && (
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}
                    aria-live="polite"
                    aria-busy="true"
                  >
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      {reasoningSteps.length > 0
                        ? `Working... (${reasoningSteps.length} step${reasoningSteps.length === 1 ? '' : 's'} completed)`
                        : 'Planning workflow...'}
                    </Typography>
                  </Box>
                )}

                {msg.error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {msg.error}
                  </Alert>
                )}

                {!msg.loading && !msg.error && response && (
                  <Box>
                    {/* Reasoning Steps */}
                    <ReasoningPlanView steps={reasoningSteps.length > 0 ? reasoningSteps : response.reasoning_steps || []} />

                    {/* Measurement Delta Chart */}
                    {(() => {
                      const structuredData = (response.metadata?.agent_data ?? response.metadata?.tool_data) as Record<string, unknown> | undefined;
                      const measurements = (structuredData?.measurements ?? response.metadata?.measurements) as import('../components/MeasurementDeltaChart').TimelineMeasurement[] | undefined;
                      if (!measurements?.length) return null;
                      return <MeasurementDeltaChart measurements={measurements} />;
                    })()}

                    {/* Answer Card */}
                    <Paper variant="outlined" sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <AutoAwesomeIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant="subtitle2" fontWeight={typography.label.fontWeight}>
                          Intelligence Answer
                        </Typography>
                        {response.intent && (
                          <Chip
                            label={response.intent.replace(/_/g, ' ')}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.62rem', ml: 'auto' }}
                          />
                        )}
                      </Box>

                      {Boolean(response.metadata?.phi_leak_detected) && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            RED ALERT: Potential PHI leak detected
                          </Typography>
                          <Typography variant="body2">
                            The AI response contained identifiable patient information and was
                            automatically scrubbed. RadNeo never sends PII to external systems
                            including LLMs. Please report this incident.
                          </Typography>
                        </Alert>
                      )}

                      <Box sx={{ lineHeight: 1.7, '& h2': { typography: 'subtitle2', fontWeight: typography.label.fontWeight, mt: 2, mb: 0.75 }, '& p': { typography: 'body2', mb: 1 }, '& ul': { pl: 2, mb: 1 }, '& li': { typography: 'body2' } }}>
                        <ReactMarkdown
                          components={{
                            h2: ({ children }) => <Typography variant="subtitle2" fontWeight={typography.label.fontWeight} sx={{ mt: 2, mb: 0.75 }}>{children}</Typography>,
                            p: ({ children }) => <Typography variant="body2" sx={{ mb: 1 }}>{children}</Typography>,
                            li: ({ children }) => <Typography component="li" variant="body2">{children}</Typography>,
                          }}
                        >
                          {response.answer}
                        </ReactMarkdown>
                      </Box>

                      <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span style={{ color: 'green', fontWeight: 700 }}>●</span>
                          RadNeo never sends PII to any external systems, including LLMs.
                        </Typography>
                      </Box>
                    </Paper>

                    {/* Follow-up suggestions */}
                    {response.follow_up_suggestions && response.follow_up_suggestions.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          Suggested follow-ups
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {response.follow_up_suggestions.map((suggestion: string, i: number) => (
                            <Chip
                              key={i}
                              label={suggestion}
                              size="small"
                              variant="outlined"
                              color="primary"
                              clickable
                              onClick={() => handleFollowUp(suggestion)}
                              sx={{ fontSize: '0.72rem' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Query Input Bar */}
        {loading && <LinearProgress sx={{ height: 2 }} />}
        <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              inputRef={inputRef}
              fullWidth
              size="small"
              placeholder={selectedPatient ? `Ask about ${selectedPatient.label}...` : 'Ask a clinical or operational question...'}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              multiline
              maxRows={3}
              sx={{ '& .MuiInputBase-root': { fontSize: '0.85rem', borderRadius: 2 } }}
            />
            <IconButton
              color="primary"
              onClick={() => handleSubmit()}
              disabled={!query.trim() || loading}
              sx={{
                bgcolor: query.trim() && !loading ? 'primary.main' : 'grey.200',
                color: query.trim() && !loading ? 'white' : 'grey.400',
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': { bgcolor: 'grey.200', color: 'grey.400' },
                width: 38,
                height: 38,
              }}
            >
              <SendIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

      </Box>

      {/* Right Rail — Evidence Panel */}
      <EvidencePanel
        sources={latestAssistantResponse?.sources || []}
        confidence={latestAssistantResponse?.confidence || 0}
      />

      {/* Study Detail Modal */}
      <StudyDetailDialog
        open={!!selectedStudyEntry}
        entry={selectedStudyEntry}
        report={selectedStudyReport}
        loading={studyDetailLoading}
        error={studyDetailError}
        onClose={handleCloseStudyDetail}
      />
    </Box>
  );
};

export default IntelligencePage;
